import { useMediaQuery } from '@workspace/hooks/commons/useMediaQuery';
import { cn } from '@workspace/lib';
import React, { useMemo } from 'react';
import { Icon } from '../Icon';
import { theme } from '../../theme';

interface PageNumbersProps {
  currentPage: number;
  totalPages: number;
  onPageClick: (page: number) => void;
  className?: string;
}

export function PageNumbers({ currentPage, totalPages, onPageClick, className }: PageNumbersProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const pageNumbers = useMemo(() => {
    // Determine max visible pages based on screen size
    // 7 for desktop, 5 for mobile (when space is tight)
    const maxVisiblePages = isDesktop ? 7 : 5;

    if (totalPages <= maxVisiblePages) {
      // If total pages fit, show them all
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // --- Calculate the "break point" for clusters ---
    // The number of pages to show before the ellipsis at the start and end.
    // For maxVisiblePages=7, we show 5 at the start and end.
    // For maxVisiblePages=5, we show 3 at the start and end.
    const startEndClusterSize = isDesktop ? 5 : 3;

    // --- BEGINNING CLUSTER ---
    // If we are close to the start, show the cluster and skip to the end.
    if (currentPage <= startEndClusterSize - 1) {
      // Example (Desktop: max=7, cluster=5): Current Page 1, 2, 3, or 4
      // We show pages 1 up to startEndClusterSize, then '...', then totalPages
      for (let i = 1; i <= startEndClusterSize; i++) {
        pages.push(i);
      }
      pages.push('...', totalPages);
      return pages;
    }

    // --- ENDING CLUSTER ---
    // If we are close to the end, show the start and skip to the end cluster.
    if (currentPage >= totalPages - (startEndClusterSize - 2)) {
      // Example (Desktop: total=15, cluster=5): Current Page 12, 13, 14, or 15
      // We show 1, then '...', then pages from totalPages-4 up to totalPages
      pages.push(1, '...');

      const startPage = totalPages - (startEndClusterSize - 1);
      for (let i = startPage; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // --- MIDDLE CLUSTER ---
    // Show the start, a leading ellipsis, the current page and its neighbors, a trailing ellipsis, and the end.
    // The pattern is: 1 ... (Current - 1) Current (Current + 1) ... totalPages
    pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    return pages;
  }, [currentPage, totalPages, isDesktop]); // Add isDesktop to the dependency array

  return (
    <div className={cn('flex items-center', className)}>
      {pageNumbers.map((page, idx) =>
        typeof page === 'number' ? (
          <button
            key={`page-${page}`}
            onClick={() => onPageClick(page)}
            className={cn(
              `flex items-center justify-center w-11 h-11 rounded font-body text-base font-semibold leading-6 cursor-pointer focus:outline-none focus:ring-0`,
              page === currentPage
                ? 'bg-primary text-white'
                : `text-secondary-400 ${theme === 'redRec' ? 'hover:text-light-green' : 'hover:text-primary'} hover:bg-secondary-100`,
            )}
          >
            {page}
          </button>
        ) : (
          <span
            key={`ellipsis-${idx}`}
            className="inline-flex w-11 h-11 justify-center items-center rounded text-black select-none"
          >
            <Icon name="ThreeDotHorizontal" />
          </span>
        ),
      )}
    </div>
  );
}
